import { useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import { Button, Table, Drawer, Form, Input, Select, message, Popconfirm, } from "antd"
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, } from "@ant-design/icons"
import { translations } from "../../utils/constants"
import { secureApi } from "../../Config/api"
import { POST, GET } from "../../utils/apis"
import { useDispatch, useSelector } from "react-redux"
import { setAllUsers, setUserPage, setUserPageSize, setUserTotal, } from "../../Redux/actions/userAction"
import { verifyToken } from "../../utils/helpers"
import paths from "../../Config/paths"
import { DeleteModal } from "../../Components"

const { Option } = Select

const Users = ({ language }) => {
    const history = useHistory()
    const dispatch = useDispatch()
    const user = useSelector((state) => state.authReducer?.user)
    const { users, page, pageSize, totalItems } = useSelector((state) => state.users)
    const authUser = useSelector((state) => state.authReducer?.user)
    const [drawerVisible, setDrawerVisible] = useState(false)
    const [form] = Form.useForm()
    const [editingUser, setEditingUser] = useState(null)
    const [search, setSearch] = useState("")
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        fetchUsers()
    }, [page, pageSize, search])

    useEffect(() => {
        verifyToken(history, dispatch)
    }, [])

    const confirmDelete = async () => {
        if (recordToDelete) {
            await handleDelete(recordToDelete);
            setIsDeleteModalOpen(false);
            setRecordToDelete(null);
        }
    }
    const fetchUsers = async () => {
        try {
            const params = {
                page: page || 1,
                limit: pageSize || 10,
                search: search || "",
            }
            const response = await secureApi?.get(GET?.GET_USERS, { params })

            if (response?.success) {
                const mapped = response?.data?.map((u) => ({
                    key: u.id,
                    id: u.id,
                    name: u.name || u.user_name,
                    email: u.email,
                    phone: u.phone || u.phone_number,
                    role: u.role,
                }))

                dispatch(setAllUsers(mapped))
                const total = response?.pagination?.totalUsers || mapped.length;
                dispatch(setUserTotal(Number(total)));

            } else {
                console.log("Failed to fetch users:", response?.message)
            }
        } catch (err) {
            message.error(translations[language]?.fetchError)
        }
    }

    const handleAddUser = async (values) => {
        try {
            setLoading(true)
            const payload = {
                userName: values.name,
                email: values.email,
                password: values.password,
                phoneNumber: values.phone,
                role: values.role,
            }
            const response = await secureApi?.post(POST?.ADD_USER, payload)

            if (response?.success) {
                message.success(translations[language]?.userAdded)
                fetchUsers()
                form.resetFields()
                setDrawerVisible(false)
            } else {
                message.error(response?.message)
            }
        } catch (error) {
            message.error(translations[language]?.addUserError)
        }
        finally {
            setLoading(false)
        }
    }

    const handleEditUser = async (values) => {
        try {
            const payload = {
                id: editingUser.id,
                userName: values.name,
                phoneNumber: values.phone,
                role: values.role,
            }

            const response = await secureApi.post(POST.EDIT_USER, payload)

            if (response?.success) {
                message.success(translations[language]?.userUpdated)
                fetchUsers()
                form.resetFields()
                setEditingUser(null)
                setDrawerVisible(false)
            } else {
                message.error(response?.message)
            }
        } catch (error) {
            message.error(translations[language]?.updateUserError)
        }
    }

    const handleRoleChange = async (key, newRole) => {
        try {
            const user = users.find((u) => u.key === key)
            if (!user) return

            const payload = {
                id: user.id,
                userName: user.name,
                phoneNumber: user.phone,
                role: newRole,
            }

            const response = await secureApi.post(POST.EDIT_USER, payload)
            if (response?.success) {
                message.success(translations[language]?.roleUpdated)
                fetchUsers()
            } else {
                message.error(response?.message)
            }
        } catch (err) {
            message.error(translations[language]?.updateRoleError)
        }
    }

    const handleDelete = async (id) => {
        try {
            const response = await secureApi.post(POST?.DELETE_USER, { id })
            if (response?.success) {
                message.success("User deleted successfully!")
                fetchUsers()
            } else {
                message.error(response?.message || "Failed to delete user")
            }
        } catch (err) {
            message.error("Error deleting user")
        }
    }

    const handleCancel = () => {
        form.resetFields()
        setEditingUser(null)
        setDrawerVisible(false)
    }

    const columns = [
        { title: translations[language]?.name, dataIndex: "name" },
        { title: translations[language]?.email, dataIndex: "email" },
        {
            title: translations[language]?.phone,
            dataIndex: "phone",
            render: (text) => text && text.trim() !== "" ? text : "-"
        }, {
            title: translations[language]?.role,
            dataIndex: "role",
            render: (role, record) => {
                const isCurrentUser = record.id === authUser?.id
                return (
                    <Select
                        value={role}
                        style={{ width: 120 }}
                        onChange={(value) => handleRoleChange(record.key, value)}
                        disabled={isCurrentUser}
                    >
                        <Option value="admin">{translations[language]?.admin}</Option>
                        <Option value="user">{translations[language]?.user}</Option>
                    </Select>
                )
            },
        },
        {
            title: translations[language]?.actions,
            render: (_, record) => {
                const isCurrentUser = record.id === authUser?.id
                return (
                    <div style={{ display: "flex", gap: 12 }}>
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            disabled={isCurrentUser}
                            onClick={() => {
                                setEditingUser(record)
                                setDrawerVisible(true)
                                form.setFieldsValue({
                                    name: record.name,
                                    email: record.email,
                                    phone: record.phone,
                                    role: record.role,
                                })
                            }}
                        />
                        <Button
                            type="link"
                            danger
                            onClick={() => { setRecordToDelete(record.id); setIsDeleteModalOpen(true) }}

                            icon={<DeleteOutlined />}
                            disabled={isCurrentUser}
                        />
                    </div>
                )
            },
        },
    ]

    return (
        <div style={{ padding: 24 }}>
            <div
                className="home-header"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "1rem",
                    alignItems: "center",
                }}
            >
                <div>
                    <h1 className="timesheet-title">
                        {translations[language]?.users}
                    </h1>
                    <p className="timesheet-subtitle">
                        {translations[language]?.userSubtitle}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                    <Input
                        placeholder={translations[language]?.searchUser}
                        prefix={<SearchOutlined />}
                        className="antd-search-input"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)
                            dispatch(setUserPage(1))
                        }}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        className="export-btn"
                        onClick={() => {
                            setEditingUser(null)
                            form.resetFields()
                            setDrawerVisible(true)
                        }}
                    >
                        {translations[language]?.addUser}
                    </Button>
                </div>
            </div>

            <div style={{ marginTop: 32 }}>
                <Table
                    columns={columns}
                    dataSource={users}
                    rowKey="key"
                    scroll={{ x: 'max-content' }}
                    className="custom-timesheet-table"
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: totalItems,
                        showSizeChanger: true,
                        onChange: (p, size) => {
                            dispatch(setUserPage(p))
                            dispatch(setUserPageSize(size))
                        },
                    }}
                />
            </div>

            <Drawer
                title={
                    editingUser
                        ? translations[language]?.editUser
                        : translations[language]?.addUser
                }
                width={720}
                open={drawerVisible}
                onClose={handleCancel}
                destroyOnClose
                footer={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "8px",
                        }}
                    >
                        <Button onClick={handleCancel} className="cancel-button">
                            {translations[language]?.cancel}
                        </Button>
                        <Button
                            loading={loading}
                            type="primary"
                            onClick={() => form.submit()}
                            className="submit-button"
                        >
                            {editingUser
                                ? translations[language]?.updateUser
                                : translations[language]?.addUser}
                        </Button>
                    </div>
                }
            >
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={editingUser ? handleEditUser : handleAddUser}
                >
                    <Form.Item
                        label={translations[language]?.name}
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: translations[language]?.nameReq,
                            },
                        ]}
                    >
                        <Input
                            className="antd-input"
                            placeholder={translations[language]?.enterName}
                        />
                    </Form.Item>

                    <Form.Item
                        label={translations[language]?.email}
                        name="email"
                        rules={[
                            {
                                required: true,
                                message: translations[language]?.emailReq,
                            },
                        ]}
                    >
                        <Input
                            type="email"
                            placeholder={translations[language]?.enterEmail}
                            className="antd-input"
                            disabled={!!editingUser}
                        />
                    </Form.Item>
                    <Form.Item
                        label={translations[language]?.phone}
                        name="phone"
                        rules={[{ pattern: /^\d*$/, message: 'Phone number can contain digits only' }]}
                    >
                        <Input
                            inputMode="numeric"
                            onChange={(event) => {
                                form.setFieldsValue({ phone: event.target.value.replace(/\D/g, '') })
                            }}
                            className="antd-input"
                            placeholder={translations[language]?.enterPhone}
                        />
                    </Form.Item>

                    {!editingUser && (
                        <Form.Item
                            label={translations[language]?.password}
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    message: translations[language]?.passwordReq,
                                },
                            ]}
                        >
                            <Input.Password
                                className="antd-input"
                                placeholder={translations[language]?.enterPassword}
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        label={translations[language]?.role}
                        name="role"
                        rules={[
                            {
                                required: true,
                                message: translations[language]?.roleReq,
                            },
                        ]}
                    >
                        <Select
                            placeholder={translations[language]?.selectRole}
                            className="antd-select"
                        >
                            <Option value="admin">
                                {translations[language]?.admin}
                            </Option>
                            <Option value="user">
                                {translations[language]?.user}
                            </Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Drawer>
            <DeleteModal
                title={translations[language]?.deleteConfirm || "Confirm Delete"}
                bodyText={translations[language]?.deleteUserMessage}
                isOpen={isDeleteModalOpen}
                language={language}
                onCancel={() => {
                    setIsDeleteModalOpen(false);
                    setRecordToDelete(null);
                }}
                onDelete={confirmDelete}
            />
        </div>
    )
}

export default Users
